import { IShoppingDaypart } from "../../models/general-setting/response";

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
const DAY_MINUTES = 24 * 60;
export const SHOPPING_DAYPART_KEY_PATTERN = /^[A-Za-z0-9_]+$/;

export const DEFAULT_SHOPPING_DAYPARTS: IShoppingDaypart[] = [
  {
    key: "night_1",
    nameAr: "خارج العمل - أول الليل",
    nameEn: "Off hours - late night",
    from: "22:00",
    to: "04:00",
  },
  {
    key: "night_2",
    nameAr: "خارج العمل - الصباح المبكر",
    nameEn: "Off hours - early morning",
    from: "04:00",
    to: "10:00",
  },
  {
    key: "morning",
    nameAr: "الفترة الصباحية",
    nameEn: "Morning",
    from: "10:00",
    to: "13:00",
  },
  {
    key: "noon",
    nameAr: "فترة الظهيرة",
    nameEn: "Noon",
    from: "13:00",
    to: "16:00",
  },
  {
    key: "afternoon",
    nameAr: "فترة بعد العصر",
    nameEn: "Afternoon",
    from: "16:00",
    to: "19:00",
  },
  {
    key: "evening",
    nameAr: "الفترة المسائية",
    nameEn: "Evening",
    from: "19:00",
    to: "22:00",
  },
];

export const getShoppingDaypartsValue = (
  dayparts?: IShoppingDaypart[] | null
) => {
  const source =
    Array.isArray(dayparts) && dayparts.length > 0
      ? dayparts
      : DEFAULT_SHOPPING_DAYPARTS;

  return sortShoppingDaypartsByStart(source.map((daypart) => ({ ...daypart })));
};

export const getShoppingDaypartLabel = (
  daypart: IShoppingDaypart,
  language?: string
) => {
  const isArabic = language?.toLowerCase().startsWith("ar");
  return isArabic
    ? daypart.nameAr || daypart.nameEn || daypart.key
    : daypart.nameEn || daypart.nameAr || daypart.key;
};

export const timeToMinutes = (value?: string) => {
  if (!value || !TIME_PATTERN.test(value)) return null;

  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
};

export const minutesToTime = (minutes: number) => {
  const normalized = ((minutes % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  const hours = Math.floor(normalized / 60)
    .toString()
    .padStart(2, "0");
  const mins = (normalized % 60).toString().padStart(2, "0");

  return `${hours}:${mins}`;
};

export const getClockDistance = (from: string, to: string) => {
  const fromMinutes = timeToMinutes(from);
  const toMinutes = timeToMinutes(to);

  if (fromMinutes === null || toMinutes === null) return 0;
  if (fromMinutes === toMinutes) return DAY_MINUTES;

  return toMinutes > fromMinutes
    ? toMinutes - fromMinutes
    : DAY_MINUTES - fromMinutes + toMinutes;
};

export const sortShoppingDaypartsByStart = (dayparts: IShoppingDaypart[]) => {
  return [...dayparts].sort((a, b) => {
    const aFrom = timeToMinutes(a.from) ?? DAY_MINUTES;
    const bFrom = timeToMinutes(b.from) ?? DAY_MINUTES;
    return aFrom - bFrom;
  });
};

export const getShoppingDaypartDuration = (daypart: IShoppingDaypart) =>
  getClockDistance(daypart.from, daypart.to);

export const getShoppingDaypartMidpoint = (daypart: IShoppingDaypart) => {
  const from = timeToMinutes(daypart.from);
  if (from === null) return daypart.from;

  const midpoint = from + Math.floor(getShoppingDaypartDuration(daypart) / 2);
  return minutesToTime(midpoint);
};

export const getShoppingDaypartTimelinePieces = (
  dayparts: IShoppingDaypart[]
) => {
  return dayparts
    .flatMap((daypart, index) => {
      const from = timeToMinutes(daypart.from);
      const to = timeToMinutes(daypart.to);

      if (from === null || to === null || from === to) return [];

      const pieces =
        from < to
          ? [{ start: from, end: to }]
          : [
              { start: from, end: DAY_MINUTES },
              { start: 0, end: to },
            ];

      return pieces.map((piece) => ({
        ...piece,
        duration: piece.end - piece.start,
        daypart,
        sourceIndex: index,
      }));
    })
    .sort((a, b) => a.start - b.start);
};

export const validateShoppingDayparts = (
  dayparts: IShoppingDaypart[] | undefined,
  messages: {
    required: string;
    invalid: string;
    duplicate: string;
    sameTime: string;
    coverage: string;
  }
) => {
  if (!Array.isArray(dayparts) || dayparts.length === 0) {
    return messages.required;
  }

  const keys = new Set<string>();
  const intervals: { start: number; end: number }[] = [];

  for (const daypart of dayparts) {
    const key = daypart?.key?.trim();
    const nameAr = daypart?.nameAr?.trim();
    const nameEn = daypart?.nameEn?.trim();
    const from = timeToMinutes(daypart?.from);
    const to = timeToMinutes(daypart?.to);

    if (
      !key ||
      !SHOPPING_DAYPART_KEY_PATTERN.test(key) ||
      !nameAr ||
      !nameEn ||
      from === null ||
      to === null
    ) {
      return messages.invalid;
    }

    if (keys.has(key)) return messages.duplicate;
    keys.add(key);

    if (from === to) return messages.sameTime;

    if (from < to) {
      intervals.push({ start: from, end: to });
    } else {
      intervals.push({ start: from, end: 24 * 60 });
      intervals.push({ start: 0, end: to });
    }
  }

  intervals.sort((a, b) => a.start - b.start || a.end - b.end);

  let cursor = 0;
  for (const interval of intervals) {
    if (interval.start !== cursor) return messages.coverage;
    cursor = interval.end;
  }

  return cursor === 24 * 60 ? true : messages.coverage;
};
